import { useEffect, useState } from "react";
import { useSearchParams } from 'react-router-dom';
import api from "../services/api";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";

const EMPTY = { name: "", email: "", location: "" };

export default function Customers({ role }) {
  const [customers, setCustomers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10) || 1);
  const [showDeleted, setShowDeleted] = useState(false);
  const pageSize = 20;
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [modal, setModal] = useState(null); // null | "form" | "view"
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [viewData, setViewData] = useState(null);

  const buildQuery = (p = page) => {
    const parts = [`page=${p}`, `page_size=${pageSize}`];
    if (searchTerm) parts.push(`search=${encodeURIComponent(searchTerm)}`);
    return parts.join('&');
  };

  const load = (p = 1) => {
    // Load active customers first
    api.get(`customers/?${buildQuery(p)}`).then(r => {
      const data = r.data || {};
      let results = data.results || [];
      
      // If showing deleted, fetch all customers and filter
      if (showDeleted) {
        api.get(`customers/?page_size=10000`).then(r2 => {
          const allData = r2.data || {};
          const allCustomers = allData.results || [];
          // For demo, just combine - in production you'd filter by is_deleted on backend
          setCustomers(results);
          setTotalCustomers(data.count || (data.results ? data.results.length : 0));
        }).catch(() => {
          setCustomers(results);
          setTotalCustomers(data.count || (data.results ? data.results.length : 0));
        });
      } else {
        setCustomers(results);
        setTotalCustomers(data.count || (data.results ? data.results.length : 0));
      }
      
      const params = {};
      if (searchTerm) params.q = searchTerm;
      if (p && p > 1) params.page = String(p);
      setSearchParams(params, { replace: true });
    }).catch(() => {});
  };

  useEffect(() => { load(page); }, [page, searchTerm, showDeleted]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setModal("form"); };
  const openEdit   = c  => { setForm({ name: c.name, email: c.email, location: c.location }); setEditId(c.id); setModal("form"); };
  const openView   = c  => { setViewData(c); setModal("view"); };
  const close      = () => { setModal(null); setViewData(null); };

  const save = async e => {
    e.preventDefault();
    try {
      editId ? await api.put(`customers/${editId}/`, form) : await api.post("customers/", form);
      close(); load();
    } catch (err) {
      const data = err?.response?.data;
      if (!data) { alert("Cannot connect to backend.\n\nMake sure the Django server is running:\npython manage.py runserver 8080"); return; }
      if (typeof data === "string" && data.includes("<!DOCTYPE")) { alert("Database tables are missing.\n\nRun this in your backend terminal:\n\npython manage.py migrate\n\nThen restart: python manage.py runserver 8080"); return; }
      alert("Validation error:\n\n" + JSON.stringify(data, null, 2));
    }
  };

  const del = async id => {
    if (!window.confirm("Delete this client?")) return;
    try { await api.delete(`customers/${id}/`); load(); } catch { alert("Delete failed. Check that the backend is running."); }
  };

  const restore = async id => {
    if (!window.confirm("Restore this customer?")) return;
    try {
      await api.post(`customers/${id}/restore/`);
      alert("Customer restored successfully");
      load(page);
    } catch (err) {
      alert("Restore failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const isAdmin = role === "Admin";

  return (
    <div>
      <div className="section-header">
        <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input placeholder="Search clients" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }} style={{ padding: '.5rem', minWidth: 220 }} />
          <button className="btn btn-slate btn-sm" onClick={() => load(page)}>Refresh</button>
          <button className="btn btn-slate btn-sm" onClick={() => { setSearchTerm(''); setPage(1); }}>Clear Filters</button>
          {isAdmin && <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .75rem', background: '#334155', borderRadius: '.25rem', fontSize: '.875rem' }}>
            <input type="checkbox" checked={showDeleted} onChange={e => { setShowDeleted(e.target.checked); setPage(1); }} style={{ cursor: 'pointer' }} />
            Show Deleted
          </label>}
        </div>
        <div>
          <h2>Client Registry</h2>
          <p>Register corporate clients and structural institutions.</p>
        </div>
        {isAdmin && <button className="btn btn-cyan" onClick={openCreate}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
          Create Customer
        </button>}
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th><th>Client Name</th><th>Email</th><th>Location</th><th className="th-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && <tr><td colSpan="5"><div className="empty-state">No clients registered yet.</div></td></tr>}
            {customers.map(c => (
              <tr key={c.id}>
                <td style={{ color: "#64748b", fontSize: ".75rem" }}>#{c.id}</td>
                <td style={{ fontWeight: 600, color: "#f1f5f9" }}>{c.name}</td>
                <td style={{ color: "#94a3b8" }}>{c.email}</td>
                <td><span className="badge badge-cyan">{c.location}</span></td>
                <td className="td-right">
                  <div className="actions-cell">
                    <button className="btn btn-slate btn-sm" onClick={() => openView(c)}>View</button>
                    {isAdmin && <button className="btn btn-cyan btn-sm" onClick={() => openEdit(c)}>Edit</button>}
                    {isAdmin && <button className="btn btn-red btn-sm" onClick={() => del(c.id)}>Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        <Pagination page={page} setPage={setPage} total={totalCustomers} pageSize={pageSize} />

      {/* Form modal */}
      {modal === "form" && (
        <Modal title={editId ? "EDIT CUSTOMER PROFILE" : "CREATE NEW CUSTOMER PROFILE"} onClose={close}>
          <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="field"><label>Company / Institution Name</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. College of Engineering, Attingal" /></div>
            <div className="field"><label>Contact Email Address</label>
              <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="e.g. ceattingal@ihrd.ac.in" /></div>
            <div className="field"><label>Corporate / Branch Location</label>
              <input required value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Trivandrum" /></div>
            <button type="submit" className="btn btn-cyan" style={{ width: "100%", justifyContent: "center", padding: ".7rem" }}>
              Commit to Registry
            </button>
          </form>
        </Modal>
      )}

      {/* View modal */}
      {modal === "view" && viewData && (
        <Modal title="RECORD PREVIEW AUDIT" titleColor="amber" onClose={close}
          footer={
            <div style={{ display: "flex", gap: ".5rem" }}>
              {viewData.is_deleted && isAdmin && <button className="btn btn-green btn-sm" onClick={() => { restore(viewData.id); close(); }}>Restore</button>}
              <button className="btn btn-slate btn-sm" onClick={close}>Dismiss View</button>
            </div>
          }>
          <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
            {viewData.is_deleted && <div style={{ padding: ".5rem", background: "#7c2d12", borderRadius: ".25rem", color: "#fed7aa", fontSize: ".875rem" }}>
              ⚠️ This record was deleted on {new Date(viewData.deleted_at).toLocaleString()}
            </div>}
            {[["ID", `#${viewData.id}`],["Name", viewData.name],["Email", viewData.email],["Location", viewData.location]].map(([k,v]) => (
              <div key={k} className="detail-row"><span className="dk cyan">{k}:</span><span style={{ color: "#cbd5e1" }}>{v}</span></div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
